import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, ArrowRight, Calendar, FileSignature } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BetaBanner from "../components/BetaBanner";
import ConsultationModal from "../components/ConsultationModal";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { trackEvent } from "../lib/analytics";
import { QelliPublicCTA } from "../components/qelli/QelliPublicCTA";
import usePageMeta from "../hooks/usePageMeta";
import M from "../lib/pageMetaConfig";

// Each paid tier has both a monthly and an annual SKU. Annual is 50% off the
// 12× monthly rate (paid up-front). Server-side prices live in /backend/billing.py
// — these strings are display-only.
const TIERS = [
  {
    id: "free",
    name: "Free",
    seats: "1 seat",
    price: { monthly: "$0", annual: "$0" },
    cadence: { monthly: "/ forever", annual: "/ forever" },
    cta: "Open scanner",
    to: { monthly: "/scan", annual: "/scan" },
    features: [
      "Unlimited wallet scans · 18 chains",
      "1 PQC contract analysis per month",
      "HTML & shareable report links (watermarked)",
      "Public methodology access",
      "30-min consultation — free",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    seats: "1 seat",
    badge: "MOST POPULAR",
    price: { monthly: "$199", annual: "$1,199" },
    cadence: { monthly: "/ month", annual: "/ year" },
    annualSavings: "Save 50% vs monthly",
    cta: "Upgrade to Pro",
    to: { monthly: "/account/billing?plan=pro_monthly", annual: "/account/billing?plan=pro_annual" },
    features: [
      "Everything in Free",
      "Unwatermarked, branded PDF reports",
      "25 PQC contract analyses per month",
      "Private workspace",
      "Priority email support",
    ],
  },
  {
    id: "team",
    name: "Team",
    seats: "6 seats",
    price: { monthly: "$999", annual: "$5,999" },
    cadence: { monthly: "/ month", annual: "/ year" },
    annualSavings: "Save 50% vs monthly",
    cta: "Upgrade to Team",
    to: { monthly: "/account/billing?plan=team_monthly", annual: "/account/billing?plan=team_annual" },
    features: [
      "Everything in Pro",
      "6 seats · shared workspace",
      "250 PQC contract analyses per month",
      "Custom-branded reports",
      "API preview (1k calls / month)",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise · Atlas",
    seats: "Unlimited seats",
    price: { monthly: "From $2,000", annual: "From $24,000" },
    cadence: { monthly: "/ month", annual: "/ year" },
    cta: "Talk to sales",
    to: { monthly: "consult", annual: "consult" },
    features: [
      "Everything in Team",
      "Unlimited PQC scans · contracts · API",
      "SSO / SAML · audit logs · SLA",
      "White-label reports",
      "Continuous PQC monitoring & alerts",
      "Dedicated strategist",
    ],
  },
];

const SERVICES = [
  {
    id: "audit", name: "QorTrace Audit", price: "From $7,500", cadence: "per engagement",
    copy: "A bespoke, board-ready PQC readiness audit for a specific wallet, protocol, treasury, or contract surface. Signed PDF + executive walkthrough.",
  },
  {
    id: "migration", name: "QorTrace Migration", price: "From $25,000", cadence: "per engagement",
    copy: "Hands-on PQC migration consulting. Hybrid signing design, signer rotation playbooks, smart-contract adapter layers, SDK integration support.",
  },
];

export default function Pricing() {
  const { t } = useTranslation();
  usePageMeta(M.pricingScanner);
  useEffect(() => {
    trackEvent("pricing_viewed", { surface: "pricing-page" });
  }, []);
  const [showConsult, setShow] = useState(false);
  const [source, setSource] = useState("pricing-page");
  const [searchParams] = useSearchParams();
  // Iter49 — honor `?cycle=annual` from /account/usage upsell links so the
  // user's intent (annual vs monthly) carries straight into the pricing page
  // without an extra click.
  const initialCycle = searchParams.get("cycle") === "annual" ? "annual" : "monthly";
  const [cycle, setCycle] = useState(initialCycle);   // "monthly" | "annual"

  // Re-sync if the user navigates between Pricing pages with different
  // ?cycle values mid-session (e.g. clicks two different upsell CTAs).
  useEffect(() => {
    const next = searchParams.get("cycle") === "annual" ? "annual" : "monthly";
    setCycle(next);
  }, [searchParams]);
  const openConsult = (s) => { setSource(s); setShow(true); };

  return (
    <div className="min-h-screen qt-radial-bg">
      <Header onConsultClick={() => openConsult("pricing-header")} />
      <BetaBanner />
      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-14">
        <div className="qt-label">{t("pricing.title", "PRICING").toUpperCase()}</div>
        <h1 className="qt-display text-5xl sm:text-6xl mt-2">
          {t("pricing.subtitle", "One platform. Priced for every stage.")}
        </h1>
        <p className="mt-4 max-w-2xl" style={{ color: "var(--qt-text-2)" }}>
          Start free. Upgrade when you need branded reports, higher contract quotas, team seats, or institutional monitoring.
          Custom audit and migration engagements available alongside any tier.
        </p>

        {/* Monthly / Annual toggle */}
        <div className="mt-8 flex items-center gap-4 flex-wrap" data-testid="pricing-cycle-toggle">
          <div
            role="tablist"
            aria-label="Billing cycle"
            className="inline-flex p-1 rounded-full"
            style={{
              background: "rgba(4,9,20,0.6)",
              border: "1px solid var(--qt-border)",
            }}
          >
            <CycleButton
              active={cycle === "monthly"}
              onClick={() => setCycle("monthly")}
              testid="pricing-cycle-monthly"
              label={t("pricing.billing_monthly", "Monthly")}
            />
            <CycleButton
              active={cycle === "annual"}
              onClick={() => setCycle("annual")}
              testid="pricing-cycle-annual"
              label={t("pricing.billing_annual", "Annual")}
              suffix={
                <span
                  className="qt-mono"
                  style={{
                    fontSize: 10,
                    letterSpacing: "0.1em",
                    background: "rgba(0,210,106,0.16)",
                    color: "#1FDD83",
                    border: "1px solid rgba(0,210,106,0.4)",
                    padding: "1px 6px",
                    borderRadius: 99,
                    marginLeft: 8,
                  }}
                >
                  {t("pricing.save_annual", "SAVE 50%").toUpperCase()}
                </span>
              }
            />
          </div>
          <span className="qt-mono text-xs" style={{ color: "var(--qt-text-3)" }}>
            {cycle === "annual"
              ? "Annual plans billed up-front · cancel anytime"
              : "Monthly plans billed every 30 days · cancel anytime"}
          </span>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {TIERS.map((t) => (
            <div key={t.id} className="qt-card qt-card-interactive flex flex-col"
                 style={t.badge ? { borderColor: "var(--qt-cyan)", boxShadow: "0 0 0 1px var(--qt-cyan)" } : {}}
                 data-testid={`tier-${t.id}`}>
              {t.badge && (
                <div className="qt-mono text-[10px] mb-3" style={{ color: "var(--qt-cyan)", letterSpacing: "0.2em" }}>
                  {t.badge}
                </div>
              )}
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <div className="qt-h2 text-xl">{t.name}</div>
                <div className="qt-mono text-[10px]" style={{ color: "var(--qt-text-3)", letterSpacing: "0.15em" }}>
                  {t.seats.toUpperCase()}
                </div>
              </div>
              <div className="flex items-baseline gap-1 mt-2">
                <div
                  className="qt-mono text-4xl"
                  data-testid={`tier-${t.id}-price`}
                  style={{ color: "#fff", fontWeight: 700, letterSpacing: "-0.04em" }}
                >
                  {t.price[cycle]}
                </div>
                <div className="qt-mono text-xs" style={{ color: "var(--qt-text-3)" }}>{t.cadence[cycle]}</div>
              </div>
              {cycle === "annual" && t.annualSavings && (
                <div
                  className="qt-mono text-[10px] mt-2"
                  data-testid={`tier-${t.id}-savings`}
                  style={{ color: "#1FDD83", letterSpacing: "0.1em" }}
                >
                  {t.annualSavings.toUpperCase()}
                </div>
              )}
              <ul className="mt-5 space-y-2 flex-1">
                {t.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "var(--qt-text-2)" }}>
                    <Check size={14} style={{ color: "var(--qt-cyan)", marginTop: 2, flexShrink: 0 }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5">
                {t.to[cycle] === "consult" ? (
                  <button onClick={() => openConsult(`pricing-tier-${t.id}`)} data-testid={`tier-${t.id}-cta`}
                          className="qt-btn qt-btn-primary w-full justify-center">
                    {t.cta} <ArrowRight size={14} />
                  </button>
                ) : (
                  <a href={t.to[cycle]} data-testid={`tier-${t.id}-cta`} className="qt-btn qt-btn-primary w-full justify-center">
                    {t.cta} <ArrowRight size={14} />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14">
          <div className="qt-label">CUSTOM ENGAGEMENTS</div>
          <h2 className="qt-h2 text-3xl mt-2">Paid audits & PQC migration</h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {SERVICES.map((s) => (
              <div key={s.id} className="qt-card" data-testid={`service-${s.id}`}>
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <div className="qt-h2 text-xl">{s.name}</div>
                  <div className="qt-mono text-xs" style={{ color: "var(--qt-cyan)" }}>
                    {s.price} · {s.cadence}
                  </div>
                </div>
                <p className="mt-3 text-sm" style={{ color: "var(--qt-text-2)" }}>{s.copy}</p>
                <button onClick={() => openConsult(`pricing-service-${s.id}`)} data-testid={`service-${s.id}-cta`}
                        className="qt-btn qt-btn-secondary mt-5" style={{ fontSize: 13 }}>
                  <Calendar size={14}/> Start with a free consultation
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 qt-card">
          <div className="flex items-start md:items-center justify-between flex-col md:flex-row gap-4">
            <div>
              <div className="qt-label">NOT SURE WHICH TIER?</div>
              <div className="qt-h2 text-2xl mt-1">Book a free 30-minute strategy call.</div>
              <p className="mt-2 text-sm" style={{ color: "var(--qt-text-2)" }}>
                We'll map your exposure, recommend the right tier, and flag whether an audit or migration engagement makes sense.
              </p>
            </div>
            <button onClick={() => openConsult("pricing-footer")} data-testid="pricing-consult-cta" className="qt-btn qt-btn-primary">
              <Calendar size={14}/> Schedule consultation
            </button>
          </div>
        </div>

        {/* Custom Enterprise — Phase 1 funnel marker. Captures intent
            BEFORE signup so PostHog `enterprise_track_intent` events
            attribute correctly. Routes to talk-to-sales with a tagged
            source so sales sees the high-intent inbound path. */}
        <div
          className="mt-12 rounded-2xl border p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-6"
          style={{
            background: "rgba(122,231,242,0.04)",
            borderColor: "rgba(122,231,242,0.28)",
          }}
          data-testid="pricing-custom-enterprise-tile"
        >
          <div className="flex-1">
            <div
              className="qt-mono text-xs uppercase tracking-[0.25em] mb-2"
              style={{ color: "#7AE7F2" }}
            >
              Custom Enterprise
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Need us to build it for you?
            </h3>
            <p className="text-sm text-white/70 max-w-[560px]">
              For institutional buyers who need more than a subscription.
              Embedded engineering, custom PQC migrations, signed deliverables,
              and a private channel to our team.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Link
              to="/audit/talk-to-sales?source=pricing-custom-enterprise"
              data-testid="pricing-custom-enterprise-cta"
              onClick={() => trackEvent("enterprise_track_intent", {
                source:        "pricing-custom-enterprise-tile",
                path:          "/pricing",
                authed:        false,           // even authed users keep the same intent semantics
                destination:   "talk-to-sales",
                expected_track: "custom_build",
              })}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap"
              style={{ background: "#7AE7F2", color: "#001018" }}
            >
              Talk to a custom-build engineer
              <ArrowRight size={14} />
            </Link>
            <Link
              to="/account/nda?view=create"
              data-testid="pricing-custom-enterprise-nda-cta"
              className="qt-mono text-[11px] inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full whitespace-nowrap"
              style={{
                background: "transparent",
                border: "1px solid rgba(122,231,242,0.40)",
                color: "#7AE7F2",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                fontWeight: 700,
              }}
            >
              <FileSignature size={12} />
              Draft mutual NDA
              <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 pb-12">
        <QelliPublicCTA variant="inline" />
      </div>
      <Footer />
      <ConsultationModal open={showConsult} onClose={() => setShow(false)} source={source} />
    </div>
  );
}

function CycleButton({ active, onClick, testid, label, suffix }) {
  return (
    <button
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testid}
      className="qt-mono"
      style={{
        padding: "8px 18px",
        borderRadius: 99,
        fontSize: 12,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        background: active ? "var(--qt-cyan)" : "transparent",
        color: active ? "#040914" : "var(--qt-text-2)",
        border: "none",
        cursor: "pointer",
        transition: "all .15s",
        display: "inline-flex",
        alignItems: "center",
        fontWeight: active ? 700 : 500,
      }}
    >
      {label}{suffix}
    </button>
  );
}
